import { useEffect, useMemo } from "react";

import { useBoolean } from "@vega-core/hooks/useBoolean";

export const DEFAULT_MAX_VISIBLE = 5;

type UseExpandableListProps<T> = {
  items: T[];
  maxVisible?: number;
  /** When this becomes falsy, collapse the list (e.g. pass `open` to reset when modal closes) */
  resetWhen?: unknown;
};

type UseExpandableListReturn<T> = {
  visibleItems: T[];
  hasMore: boolean;
  remainingCount: number;
  isExpanded: boolean;
  toggle: () => void;
};

export function useExpandableList<T>({
  items,
  maxVisible = DEFAULT_MAX_VISIBLE,
  resetWhen,
}: UseExpandableListProps<T>): UseExpandableListReturn<T> {
  const { value: isExpanded, toggle, setFalse: collapse } = useBoolean(false);

  useEffect(() => {
    if (resetWhen !== undefined && resetWhen === false) {
      collapse();
    }
  }, [resetWhen, collapse]);

  const hasMore = useMemo(
    () => items.length > maxVisible,
    [items.length, maxVisible],
  );

  const remainingCount = useMemo(
    () => Math.max(0, items.length - maxVisible),
    [items.length, maxVisible],
  );

  const visibleItems = useMemo(
    () => (isExpanded ? items : items.slice(0, maxVisible)),
    [isExpanded, items, maxVisible],
  );

  return {
    visibleItems,
    hasMore,
    remainingCount,
    isExpanded,
    toggle,
  };
}
